import { useMemo } from "react";

import {
  Badge,
  Table,
  TableColumnDefinition,
  TableQuery,
  TableRowDefinition,
  FontIcon,
  Typography,
} from "@ezmid/components-library/";
import {
  faEnvelope,
  faFingerprint,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetBackOfficeListLecturersQuery } from "../../../api/lecturers.generated";
import { useFormatters } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { truncateWithEllipsis } from "../../../utils/stringUtils";
import { AllRoutes } from "../../route/Route";

const localStorageKey = "lecturer.page";

type TableData = {
  id: string;
  fullName: string;
  contactEmail: string;
  description: string;
  createdAt: string;
};

export function LecturersListPageContent<T extends object>() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const { query, updateQuery } = useTableQuery(
    localStorageKey,
    {
      pageSize: 10,
      pageIndex: 1,
      keyword: "",
      sortBy: { key: "name", order: "desc" },
    },
    AllRoutes.LECTURERS_LIST
  );

  const { currentData, isFetching } = useGetBackOfficeListLecturersQuery({
    page: query?.pageIndex,
    size: query?.pageSize,
    keyword: query?.keyword,
    sort: [`${query.sortBy?.key}:${query.sortBy?.order}`],
  });

  const langCode = i18n.language.split("-")[0];

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.fullName"),
        id: "name",
        accessor: "fullName",
      },
      {
        Header: t("table.header.userID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.id.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "id",
        accessor: "id",
        width: "250px",
      },

      {
        Header: t("table.header.email"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faEnvelope} />}
              />
            }
            status="break"
          >
            {row?.original.contactEmail}
          </Badge>
        ),
        id: "email",
        accessor: "email",
        width: "250px",
      },
      {
        Header: t("table.header.description"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          return (
            <Typography variant="body1">
              {truncateWithEllipsis(row?.original.description, 30)}
            </Typography>
          );
        },
        id: "description",
        accessor: "description",
        width: "250px",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
        width: "250px",
      },
    ],
    []
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((lecturer) => {
        const translatedDescription = lecturer?.translations?.find(
          (translation) => translation?.language === langCode
        )?.description;
        return {
          id: lecturer?.id || "",
          fullName: lecturer?.fullName || "",
          contactEmail: lecturer?.contactEmail || "",
          createdAt: lecturer?.createdAt || "",
          description: translatedDescription || "",
        };
      }) || [],
    [currentData]
  );

  const onFetchData = ({
    pageIndex,
    pageSize,
    keyword: queryKeyword,
    sortBy,
  }: TableQuery) => {
    updateQuery({
      pageIndex,
      pageSize,
      keyword: queryKeyword || "",
      sortBy,
    });
  };

  return (
    <div className="w-full min-w-full p-2">
      <div>
        <Table
          tableData={memoizedData}
          tableColumns={columns}
          showPagination
          totalCountOfRecords={currentData?.count || 0}
          showToolbar
          onRowSelect={(row) =>
            navigate(
              makeRoute(AllRoutes.LECTURER_DETAIL, { id: row.original?.id })
            )
          }
          onFetchData={onFetchData}
          query={{
            pageIndex: query.pageIndex,
            pageSize: query.pageSize,
            keyword: query.keyword,
            sortBy: query.sortBy,
          }}
          isLoading={isFetching}
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          shouldAddEmptyRowsOnLastPage={currentData?.last}
        />
      </div>
    </div>
  );
}
